import React, { useState, useEffect } from 'react'
import { useSheetDragDismiss } from '../hooks/useSheetDragDismiss'
import SheetHeader from './SheetHeader'
import { setAppStatusBarDark } from '../utils/flutterStatusBar'
import { bookmarkApi } from '../api/bookmark'
import LoadingSpinner from './LoadingSpinner'
import PlateList from './PlateList'
import { ReviewCategory } from '../api/user'

interface PlaylistFilterSheetProps {
  isOpen: boolean
  onClose: () => void
  /** 플레이트리스트 선택 시 (지도 필터로 사용) */
  onSelect: (playlistId: number, name: string) => void
}

// 지도 세션 동안 재사용하는 캐시 — 시트 열 때 스피너 없이 카드가 바로 뜨도록.
let cachedPlaylists: ReviewCategory[] | null = null

// /bookmarks/categories: 커버(thumbnail_image) 풀URL 포함 + 기본 폴더 제외.
// (getBookmarks(/bookmarks)는 커버를 안 채워서 커버가 안 뜸)
const fetchPlaylists = async (): Promise<ReviewCategory[]> => {
  const res = await bookmarkApi.getBookmarkCategories()
  return res.categories.map((c) => ({
    id: c.id,
    name: c.name,
    thumbnail_image: c.thumbnail_image ?? null,
    is_public: c.is_public,
    created_at: c.created_at ?? '',
  }))
}

/**
 * 지도 진입 시 미리 호출해두면 시트가 스피너 없이 바로 뜬다.
 * 고를 수 있는 리스트 개수를 돌려준다 — 0이면 지도에서 '플레이트리스트' 칩을 숨긴다.
 * 못 받아오면 null (모름 → 칩은 그대로 둔다).
 */
export const prefetchPlaylistFilter = async (): Promise<number | null> => {
  try {
    cachedPlaylists = await fetchPlaylists()
    return cachedPlaylists.length
  } catch {
    // 무시 — 시트 열 때 다시 시도
    return null
  }
}

const samePlaylists = (a: ReviewCategory[], b: ReviewCategory[]): boolean =>
  a.length === b.length &&
  a.every(
    (p, i) =>
      p.id === b[i].id &&
      p.name === b[i].name &&
      p.thumbnail_image === b[i].thumbnail_image
  )

/**
 * 지도 필터 "플레이트리스트" 전용 선택 시트.
 * 마이 화면과 동일한 플레이트 카드(좌우 스크롤) UI를 재사용.
 * 하나 선택하면 그 리스트의 장소만 지도에 표시.
 */
const PlaylistFilterSheet: React.FC<PlaylistFilterSheetProps> = ({
  isOpen,
  onClose,
  onSelect,
}) => {
  // 캐시가 있으면 그걸로 시작(스피너 없음), 없으면 로딩 표시
  const [playlists, setPlaylists] = useState<ReviewCategory[]>(
    cachedPlaylists ?? []
  )
  const [isLoading, setIsLoading] = useState(cachedPlaylists === null)

  useEffect(() => {
    if (!isOpen) return
    let aborted = false
    const load = async () => {
      // 캐시 있으면 즉시 카드 표시 + 백그라운드 갱신(스피너 X)
      if (cachedPlaylists !== null) {
        setPlaylists(cachedPlaylists)
        setIsLoading(false)
      } else {
        setIsLoading(true)
      }
      try {
        const opts = await fetchPlaylists()
        if (aborted) return
        cachedPlaylists = opts
        // 내용이 같으면 상태 갱신 안 함 → Splide 재마운트로 인한 깜빡임 방지
        setPlaylists((prev) => (samePlaylists(prev, opts) ? prev : opts))
      } catch (error) {
        if (!aborted && cachedPlaylists === null) setPlaylists([])
      } finally {
        if (!aborted) setIsLoading(false)
      }
    }
    load()
    return () => {
      aborted = true
    }
  }, [isOpen])

  useEffect(() => {
    setAppStatusBarDark(isOpen)
    return () => setAppStatusBarDark(false)
  }, [isOpen])

  const { handleProps, sheetStyle } = useSheetDragDismiss({
    onClose,
    enabled: isOpen,
  })

  if (!isOpen) return null

  const handlePlateClick = (id: string) => {
    const p = playlists.find((x) => String(x.id) === id)
    if (p) onSelect(p.id, p.name)
  }

  return (
    <>
      {/* 오버레이 */}
      <div
        className="fixed inset-0 bg-black bg-opacity-40 z-[1040]"
        onClick={onClose}
      />

      {/* 바텀 시트 */}
      <div className="fixed bottom-0 left-0 right-0 z-[1050] flex justify-center">
        <div className="app-container flex flex-col" style={sheetStyle}>
          <div className="bg-ivoryKey rounded-t-30px shadow-lg overflow-hidden flex flex-col w-full">
            {/* 드래그 핸들 */}
            <div className="flex flex-col w-full" {...handleProps}>
              <div className="w-40px h-5px bg-d9d9d9 rounded-20px mx-auto mt-16px mb-16px"></div>
            </div>

            <SheetHeader title="플레이트리스트 선택" />

            {/* 마이 화면과 동일한 플레이트 카드 좌우 스크롤. 카드 위·아래 28px.
                로딩→카드 높이 튐(지지직) 방지로 카드영역 높이 예약. */}
            <div className="w-full flex flex-col justify-center pt-28px pb-28px min-h-[236px]">
              {isLoading ? (
                <div className="w-full flex items-center justify-center py-40px">
                  <LoadingSpinner />
                </div>
              ) : playlists.length === 0 ? (
                <div className="w-full text-center py-40px text-style-body1 text-textColorText3">
                  플레이트리스트가 없습니다
                </div>
              ) : (
                <PlateList
                  categories={playlists}
                  onCategoryClick={handlePlateClick}
                  isMine={false}
                  hideHeader
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default PlaylistFilterSheet
